import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCommentDots,
  faCog,
  faFolderOpen,
  faPlus,
  faUsers,
  faPaperPlane,
  faCalculator,
  faSignOutAlt,
  faUserCircle,
  faFileLines,
  faPlay,
  faMobileAlt,
  faChevronLeft,
  faChevronRight,
  faUpload,
  faFileContract,
  faRobot,
  faChartLine,
  faSync,
  faComputer
} from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash";
import { toast } from "react-toastify";

import useChat from "../../stores/useChat";
import useAppSession from "../../stores/useAppSession";
import fileApi from "../../services/api/fileApi";
import EvaLogo from "../../images/EVA.png";
import Mundo365 from "../../images/mundo.jfif";

import FileUploadModal from "../FileUploadModal/FileUploadModal.jsx";
import FileListModal from "../FileListModal/FileListModal.jsx";
import YouTubeModal from "../YoutubeModal/YoutubeModal.jsx";
import TelegramModal from "../TelegrantokenModal/TelegrantokenModal.jsx";
import ContractUploadModal from "../ContratosUpload/FileUploadModal.jsx";

const CustomSwitch = ({ checked, onChange }) => (
  <SwitchWrapper checked={checked} onClick={() => onChange(!checked)}>
    <SwitchHandle checked={checked} />
  </SwitchWrapper>
);

const CustomSelect = ({ value, onChange, options, width }) => (
  <StyledSelect value={value} onChange={(e) => onChange(e.target.value)} style={{ width }}>
    {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
  </StyledSelect>
);

const CustomInputNumber = ({ value, onChange, min, max, step = 1, width }) => (
  <StyledInputNumber
    type="number"
    value={value}
    min={min}
    max={max}
    step={step}
    onChange={(e) => onChange(Number(e.target.value))}
    style={{ width }}
  />
);

const SidebarWrapper = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: #f2f2f2;
`;

const NavRail = styled.aside`
  width: 68px;
  background: #ffffff;
  border-right: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  gap: 20px;
  z-index: 100;
  flex-shrink: 0;
  .logo { width: 42px; height: 42px; object-fit: contain; border-radius: 6px; }
  .mundo365 { width: 32px; height: 32px; border-radius: 50%; opacity: 0.8; cursor: pointer; &:hover { opacity: 1; }}
`;

const NavItem = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: ${props => props.active ? "#fff" : "#86868b"};
  background: ${props => props.active ? "#ff8f40" : "transparent"};
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s;
  &:hover { color: ${props => props.active ? "#fff" : "#ff8f40"}; background: ${props => props.active ? "#ff8f40" : "#f5f5f7"}; }
`;

const DetailPanel = styled.div`
  width: ${props => props.isOpen ? "320px" : "0"};
  background: #ffffff;
  border-right: 1px solid #eaeaea;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  .panel-header {
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #f5f5f7;
    h2 { font-size: 18px; font-weight: 700; margin: 0; color: #1d1d1f; }
  }
  .panel-content {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
  }
  h3 { font-size: 11px; font-weight: 700; color: #86868b; text-transform: uppercase; margin: 24px 0 12px; letter-spacing: 0.5px; }
`;

const MenuAction = styled.div`
  padding: 12px;
  border-radius: 6px;
  color: #333;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  &:hover { background: #f5f5f7; color: #ff8f40; }
  svg { width: 16px; color: #86868b; }
`;

const ConfigRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 8px 4px;
  .item { display: flex; justify-content: space-between; align-items: center; font-size: 14px; color: #444; }
`;

const SwitchWrapper = styled.div`
  width: 34px; height: 18px; background: ${props => props.checked ? "#ff8f40" : "#d2d2d7"};
  border-radius: 10px; position: relative; cursor: pointer; transition: 0.2s;
`;

const SwitchHandle = styled.div`
  width: 14px; height: 14px; background: #fff; border-radius: 50%;
  position: absolute; top: 2px; left: ${props => props.checked ? "18px" : "2px"}; transition: 0.2s;
`;

const StyledSelect = styled.select`
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  padding: 2px;
  outline: none;
`;

const StyledInputNumber = styled.input`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
  outline: none;
`;

const TopNavbar = styled.nav`
  height: 45px;
  background: #ffffff;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  align-items: center;
  padding: 0 24px;
  h1 { font-size: 16px; font-weight: 600; color: #1d1d1f; margin: 0; }
  .status-dot { width: 8px; height: 8px; background: #34c759; border-radius: 50%; margin-right: 12px; }
`;

const Sidebar = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const chat = useChat();
  const session = useAppSession();
  const { setSessionId, logout, token, clientId } = session;

  const [activeTab, setActiveTab] = useState("chatbot");
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [youtubeUrl, setYouTubeUrl] = useState([]);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isFileListModalOpen, setIsFileListModalOpen] = useState(false);
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [isContractUploadModalOpen, setIsContractUploadModalOpen] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/contratos")) {
      setActiveTab("contratos");
    } else if (path.includes("/expense-management")) {
      setActiveTab("contas");
    } else if (path.includes("/dashboard")) {
      setActiveTab("dashboard");
    } else {
      setActiveTab("chatbot");
    }
  }, [location.pathname]);

  useEffect(() => { chat.loadSettingsFromAPI(); }, []);

  const handleNewChat = useCallback(() => {
    chat.setShouldLog(true);
    chat.clearMessages();
    chat.setSession("", true);
    setSessionId("");
    chat.clearSession();
    chat.setFimConversa(false);
    navigate("/");
    window.location.reload();
  }, [navigate, setSessionId, chat]);

  const fetchFiles = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = activeTab === "contratos"
        ? await fileApi.contrato_getFiles(clientId)
        : await fileApi.getFiles(clientId);

      if (response && response.status === 200) {
        const data = response.data || {};
        const arrFiles = Object.entries(data).map(([file, date]) => ({ file, date }));
        setFiles(arrFiles);
      }
    } catch {
      toast.error("Erro ao carregar arquivos");
    } finally {
      setLoading(false);
    }
  }, [clientId, activeTab]);

  useEffect(() => {
    if (isFileListModalOpen) fetchFiles();
  }, [isFileListModalOpen, fetchFiles]);

  const handleFileUpload = async (uploadFiles) => {
    if (uploadFiles?.length > 0) {
      setLoading(true);
      try {
        await Promise.all(uploadFiles.map((file) => fileApi.uploadFile(file, clientId)));
        const ret = await fileApi.processFile(clientId, chat.session);
        if (ret?.status === 200) {
          toast.success("Arquivo processado com sucesso!");
          fetchFiles();
        }
      } catch {
        toast.error("Erro ao fazer upload dos arquivos.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleContractUpload = async (clientId, uploadFiles) => {
    if (uploadFiles?.length > 0) {
      setLoading(true);
      try {
        await Promise.all(uploadFiles.map((file) => fileApi.contrato_uploadFile(file, clientId)));
        const ret = await fileApi.contrato_processFile(clientId, chat.session);
        if (ret?.status === 200) {
          toast.success("Contratos processados com sucesso!");
          if (isFileListModalOpen) fetchFiles();
        }
      } catch {
        toast.error("Erro ao processar contratos.");
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteFile = async (fileId) => {
    setLoading(true);
    try {
      const decoded = token ? jwtDecode(token) : null;
      const environmentid = decoded?.user_access?.environment?.environmentid;

      if (activeTab === "contratos") {
        await fileApi.contrato_deleteFile({ clientId, environmentid }, fileId);
      } else {
        await fileApi.deleteFile({ clientId, environmentid }, fileId);
      }

      setFiles((prev) => prev.filter((f) => f.file !== fileId));
      toast.success("Arquivo removido com sucesso.");
    } catch {
      toast.error("Ocorreu um erro ao remover o arquivo.");
    } finally {
      setLoading(false);
    }
  };

  const inputDebounce = useCallback(
    debounce((time) => chat.setInactivityTimeout(time), 350),
    [chat]
  );

  const decodedToken = token ? jwtDecode(token) : null;
  const workspaceName = decodedToken?.user_access?.environment?.workspace?.name || "Workspace";

  const canShowConfig =
        decodedToken?.roles?.includes("ADMIN") ||
        decodedToken?.roles?.includes("ADMIN_IA");


  return (
    <SidebarWrapper>
      <NavRail>
        <img src={EvaLogo} className="logo" alt="EVA" />

        <NavItem active={activeTab === "dashboard"} onClick={() => navigate("/dashboard")}>
          <FontAwesomeIcon icon={faChartLine} />
        </NavItem>

        <NavItem active={activeTab === "chatbot"} onClick={() => { setActiveTab("chatbot"); setIsPanelOpen(true); navigate("/"); }}>
          <FontAwesomeIcon icon={faRobot} />
        </NavItem>

        <NavItem active={activeTab === "contas"} onClick={() => { setActiveTab("contas"); setIsPanelOpen(true); navigate("/expense-management"); }}>
          <FontAwesomeIcon icon={faCalculator} />
        </NavItem>

        <NavItem active={activeTab === "contratos"} onClick={() => { setActiveTab("contratos"); setIsPanelOpen(true); navigate("/contratos"); }}>
          <FontAwesomeIcon icon={faFileContract} />
        </NavItem>

        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
          <a href="https://mundo365.com.br/" target="_blank" rel="noreferrer">
            <img style={{ width: 22, height: "auto" }} className="mundo365" src={Mundo365} alt="Mundo 365" />
          </a>
          <NavItem active={location.pathname === "/user-profile"} onClick={() => navigate("/user-profile")}><FontAwesomeIcon icon={faUserCircle} /></NavItem>
          <NavItem onClick={logout}><FontAwesomeIcon icon={faSignOutAlt} style={{ color: "#ff4d4f" }} /></NavItem>
        </div>
      </NavRail>

      <DetailPanel isOpen={isPanelOpen}>
        <div className="panel-header">
          <h2>{activeTab === "chatbot" ? "Chatbot" : activeTab === "contas" ? "Contas" : activeTab === "contratos" ? "Contratos" : "Dashboard"}</h2>
          <FontAwesomeIcon icon={faChevronLeft} style={{ cursor: "pointer", color: "#86868b" }} onClick={() => setIsPanelOpen(false)} />
        </div>

        <div className="panel-content">
          {(activeTab === "chatbot" || activeTab === "dashboard") && (
            <>
              <MenuAction onClick={handleNewChat}><FontAwesomeIcon icon={faPlus} /> Novo Chat</MenuAction>
              <MenuAction onClick={() => navigate("/messageboard")}><FontAwesomeIcon icon={faCommentDots} /> Mensagens</MenuAction>
              
              {canShowConfig && (
              <>
                <MenuAction onClick={() => navigate("/workspace")}><FontAwesomeIcon icon={faUsers} /> Equipe</MenuAction>

                <h3>Canais</h3>
                <MenuAction onClick={() => navigate("/whatsapp")}><FontAwesomeIcon icon={faMobileAlt} /> Whatsapp</MenuAction>
                <MenuAction onClick={() => setIsTelegramModalOpen(true)}><FontAwesomeIcon icon={faPaperPlane} /> Telegram</MenuAction>
                <MenuAction onClick={() => setIsYoutubeModalOpen(true)}><FontAwesomeIcon icon={faPlay} /> YouTube</MenuAction>
                <MenuAction onClick={() => navigate("/widget")}><FontAwesomeIcon icon={faComputer} /> Web Widget</MenuAction>

                <h3>Arquivos</h3>
                <MenuAction onClick={() => setIsUploadModalOpen(true)}><FontAwesomeIcon icon={faUpload} /> Carregar arquivos</MenuAction>
                <MenuAction onClick={() => setIsFileListModalOpen(true)}><FontAwesomeIcon icon={faFileLines} /> Mostrar arquivos</MenuAction>

                <h3>Configurações</h3>
                <ConfigRow>
                  <div className="item"><span>Coleta de dados</span><CustomSwitch checked={chat.perguntas_guiadas} onChange={chat.setPerguntasGuiadas} /></div>
                  <div className="item"><span>Imagens</span><CustomSwitch checked={chat.habilitaImagens} onChange={chat.setHabilitaImagens} /></div>
                  <div className="item"><span>E-mail</span><CustomSwitch checked={chat.habilitaEmail} onChange={chat.setHabilitaEmail} /></div>
                  <div className="item"><span>Zendesk</span><CustomSwitch checked={chat.habilitaZendesk} onChange={chat.setHabilitaZendesk} /></div>
                  <div className="item"><span>Somente contexto</span><CustomSwitch checked={chat.habilitaContexto} onChange={chat.setHabilitaContexto} /></div>
                </ConfigRow>
                <h3>Engine IA</h3>
                <ConfigRow>
                  <div className="item"><span>Engine</span><CustomSelect width="90px" value={chat.engine} onChange={chat.setEngine} options={[{ value: 'azure', label: 'Azure' }, { value: 'openai', label: 'OpenAI' }]} /></div>
                  <div className="item"><span>Temperatura</span>
                    <CustomSelect width="60px" value={Number(chat.temperature)} onChange={(value) => chat.setTemperature(Number(value))} options={Array.from({ length: 11 }, (_, i) => ({ value: i * 0.1, label: Number(i * 0.1).toFixed(1) }))} />
                  </div>
                  <div className="item"><span>Inatividade (s)</span><CustomInputNumber width="60px" min={10} value={chat.inactivityTimeout} onChange={inputDebounce} /></div>
                  <div className="item"><span>Docs vazios</span><CustomInputNumber width="60px" min={0} value={chat.docs_vazios_fim_conversa} onChange={chat.setDocsVaziosFimConversa} /></div>
                </ConfigRow>
                <h3>Busca & Enriquecimento</h3>
                <ConfigRow>
                  <div className="item"><span>Enriquecimento</span><CustomSwitch checked={chat.richPrompt} onChange={chat.setRichPrompt} /></div>
                  <div className="item"><span>Palavras</span><CustomInputNumber width="60px" min={1} max={20} value={chat.richPrompt_qtd} onChange={chat.setRichPromptQtd} /></div>
                  <div className="item"><span>Opção Numérica</span><CustomSwitch checked={chat.opcaoNum} onChange={chat.setOpcaoNum} /></div>
                  <div className="item"><span>Qtd Docs</span><CustomInputNumber width="60px" min={1} max={10} value={chat.qtdDoc} onChange={chat.setQtdDoc} /></div>
                  <div className="item"><span>Aproximidade</span><CustomInputNumber width="60px" min={0} max={110} step={1} value={chat.nearDoc} onChange={chat.setNearDoc} /></div>
                </ConfigRow>
              </>
            )}
            </>
          )}

          {activeTab === "contas" && (
            <>
              <MenuAction onClick={() => navigate("/expense-management")}><FontAwesomeIcon icon={faCalculator} /> Gestão de Contas</MenuAction>
            </>
          )}

          {activeTab === "contratos" && (
            <>
              <MenuAction onClick={() => navigate("/contratos")}><FontAwesomeIcon icon={faFileContract} /> Gestão de Contratos</MenuAction>
              <MenuAction onClick={() => setIsContractUploadModalOpen(true)}><FontAwesomeIcon icon={faUpload} /> Subir Contratos</MenuAction>
            </>
          )}
        </div>
      </DetailPanel>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        <TopNavbar>
          <div style={{ display: "flex", alignItems: "center" }}>
            {!isPanelOpen && <FontAwesomeIcon icon={faChevronRight} style={{ marginRight: 20, cursor: "pointer", color: "#86868b" }} onClick={() => setIsPanelOpen(true)} />}
            <div className="status-dot" />
            <h1>{workspaceName}</h1>
          </div>
        </TopNavbar>
        <div style={{ flex: 1, overflow: "auto", background: "#f2f2f4" }}>{children}</div>
      </main>

      <FileUploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} onFileUpload={handleFileUpload} loading={loading} />

      <ContractUploadModal
        isOpen={isContractUploadModalOpen}
        onClose={() => setIsContractUploadModalOpen(false)}
        onFileUpload={handleContractUpload}
        clientId={clientId}
      />

      <FileListModal isOpen={isFileListModalOpen} onClose={() => setIsFileListModalOpen(false)} files={files} onDeleteFile={deleteFile} loading={loading} />
      <YouTubeModal isOpen={isYoutubeModalOpen} onClose={() => setIsYoutubeModalOpen(false)} youtubeUrls={youtubeUrl} setYouTubeUrls={setYouTubeUrl} />
      <TelegramModal isOpen={isTelegramModalOpen} onClose={() => setIsTelegramModalOpen(false)} />
    </SidebarWrapper>
  );
};

export default Sidebar;
